import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchData,
  addNotificationAction,
  editNotificationAction,
  deleteNotificationAction,
} from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faPlus,
  faTrashAlt,
  faEdit,
  faInfoCircle,
  faSyncAlt,
  faExclamationTriangle,
  faCalendarAlt,
  faLink,
  faHeading,
  faParagraph,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";

const Notifications = () => {
  const dispatch = useDispatch();
  const notificationsData = useSelector(
    (state) => state.main.data?.notificationsData || []
  );

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newTitle, setNewTitle] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newType, setNewType] = useState("info");
  const [newLink, setNewLink] = useState("");

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editType, setEditType] = useState("info");
  const [editLink, setEditLink] = useState("");

  const [selectedItems, setSelectedItems] = useState([]);
  const [animateIn, setAnimateIn] = useState(false);

  const messageRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await dispatch(fetchData(true));
      setLoading(false);
    };
    loadData();
  }, [dispatch]);

  useEffect(() => {
    if (showAddForm || showEditForm) {
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [showAddForm, showEditForm]);

  const handleAdd = () => {
    setNewTitle("");
    setNewMessage("");
    setNewType("info");
    setNewLink("");
    setShowAddForm(true);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setEditTitle(item.title);
    setEditMessage(item.message);
    setEditType(item.type);
    setEditLink(item.link);
    setShowEditForm(true);
  };

  const handleSubmitNew = async () => {
    if (!newTitle.trim() || !newMessage.trim()) {
      toast.error("يرجى ملء العنوان والرسالة");
      return;
    }

    const loadingToast = toast.loading("جارٍ إضافة التنبيه...");
    try {
      await dispatch(
        addNotificationAction({
          title: newTitle,
          message: newMessage,
          type: newType,
          link: newLink,
        })
      );
      toast.success("تم إضافة التنبيه بنجاح", { id: loadingToast });
      setShowAddForm(false);
    } catch (error) {
      toast.error("فشل إضافة التنبيه", { id: loadingToast });
    }
  };

  const handleSubmitEdit = async () => {
    if (!editTitle.trim() || !editMessage.trim()) {
      toast.error("يرجى ملء العنوان والرسالة");
      return;
    }

    const loadingToast = toast.loading("جارٍ تحديث التنبيه...");
    try {
      await dispatch(
        editNotificationAction({
          id: editId,
          title: editTitle,
          message: editMessage,
          type: editType,
          link: editLink,
        })
      );
      toast.success("تم تحديث التنبيه بنجاح", { id: loadingToast });
      setShowEditForm(false);
    } catch (error) {
      toast.error("فشل التحديث", { id: loadingToast });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    toast(
      (t) => (
        <div className="confirm-toast">
          <p>هل أنت متأكد من حذف {selectedItems.length} تنبيه؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const l = toast.loading("جارٍ الحذف...");
                try {
                  for (let id of selectedItems) {
                    await dispatch(deleteNotificationAction(id));
                  }
                  setSelectedItems([]);
                  toast.success("تم الحذف بنجاح");
                } catch {
                  toast.error("فشل الحذف");
                } finally {
                  toast.dismiss(l);
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
            >
              نعم، احذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "alert":
        return faExclamationTriangle;
      case "update":
        return faSyncAlt;
      default:
        return faInfoCircle;
    }
  };

  return (
    <div className="notifications-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        {/* Premium Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faBell} />
            </div>
            <div className="header-text-box">
              <h2>التنبيهات لمصمم الشهادات</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة الإشعارات المباشرة</span>
              </div>
            </div>
          </div>

          <div className="header-tools">
            <Can module="notifications" action="add">
              <button onClick={handleAdd} className="premium-action-btn">
                <FontAwesomeIcon icon={faPlus} />
                <span className="btn-text">إضافة تنبيه جديد</span>
              </button>
            </Can>

            <Can module="notifications" action="delete">
              <button
                className={`bulk-delete-btn-inline ${
                  selectedItems.length > 0 ? "show" : ""
                }`}
                onClick={handleDeleteSelected}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span className="btn-text">حذف ({selectedItems.length})</span>
              </button>
            </Can>
          </div>
        </div>

        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل التنبيهات...</span>
            </div>
          ) : (
            <table className="modern-condensed-table">
              <thead>
                <tr>
                  <th width="40">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedItems(notificationsData.map((n) => n.id));
                        else setSelectedItems([]);
                      }}
                      checked={
                        selectedItems.length === notificationsData.length &&
                        notificationsData.length > 0
                      }
                    />
                  </th>
                  <th>محتوى التنبيه</th>
                  <th width="140">نوع التنبيه</th>
                  <th width="180">تاريخ النشر</th>
                  <th width="100" style={{ textAlign: "center" }}>
                    تعديل التنبيه
                  </th>
                </tr>
              </thead>
              <tbody>
                {notificationsData.length > 0 ? (
                  notificationsData.map((item) => (
                    <tr
                      key={item.id}
                      className={
                        selectedItems.includes(item.id) ? "row-selected" : ""
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleSelectItem(item.id)}
                        />
                      </td>
                      <td
                        className="info-cell"
                        onClick={() => handleEdit(item)}
                      >
                        <div className="avatar-minimal">
                          <FontAwesomeIcon icon={getTypeIcon(item.type)} />
                        </div>
                        <div className="meta-minimal">
                          <span className="name">{item.title}</span>
                          <span className="code">
                            {item.message.substring(0, 150)}
                            {item.message.length > 150 ? "..." : ""}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`type-badge-alt ${item.type}`}>
                          {item.type === "update"
                            ? "تحديث جديد"
                            : item.type === "info"
                            ? "معلومات"
                            : ""}
                        </span>
                      </td>
                      <td>
                        <div className="date-display">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{item.date}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="row-toggle-btn"
                          onClick={() => handleEdit(item)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="empty-ui-state">
                      <FontAwesomeIcon icon={faBell} className="icon" />
                      <h3>لا توجد تنبيهات حالياً</h3>
                      <p>
                        ابدأ بإضافة أول تنبيه لمصممي الشهادات من زر الإضافة
                        أعلاه
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Card View */}
        <div className="mobile-card-view">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : notificationsData.length > 0 ? (
            notificationsData.map((item) => (
              <div className="mobile-card" key={item.id}>
                <div className="mobile-card-header">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={getTypeIcon(item.type)}
                      style={{ color: "var(--accent)" }}
                    />
                    <span className="mobile-card-title">{item.title}</span>
                  </div>
                  <span className={`type-badge-alt ${item.type}`}>
                    {item.type === "update"
                      ? "تحديث"
                      : item.type === "info"
                      ? "معلومات"
                      : "تنبيه"}
                  </span>
                </div>
                <div className="mobile-card-body">
                  <p style={{ margin: 0, lineHeight: "1.5" }}>
                    {item.message.substring(0, 150)}
                    {item.message.length > 150 ? "..." : ""}
                  </p>
                  <div className="mobile-card-actions" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="premium-action-btn"
                          onClick={() => handleEdit(item)}
                          style={{
                            padding: "8px 14px",
                            fontSize: "12px",
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          <span>تعديل</span>
                        </button>
                        <button
                          className="premium-action-btn"
                          onClick={async () => {
                            if (window.confirm("حذف هذا التنبيه؟")) {
                              await dispatch(deleteNotificationAction(item.id));
                              toast.success("تم الحذف");
                            }
                          }}
                          style={{
                            padding: "8px 14px",
                            fontSize: "12px",
                            background: "#ef4444",
                          }}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                          <span>حذف</span>
                        </button>
                      </div>
                      <div className="date-display" style={{ fontSize: "12px", margin: 0 }}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>{item.date}</span>
                      </div>
                   </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-ui-state">
              <FontAwesomeIcon icon={faBell} className="icon" />
              <h3>لا توجد تنبيهات</h3>
            </div>
          )}
        </div>
      </div>

      {/* Premium Modal for Add/Edit */}
      {(showAddForm || showEditForm) && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card">
            <div className="modal-header">
              <div className="icon">
                <FontAwesomeIcon icon={showAddForm ? faPlus : faEdit} />
              </div>
              <h3>
                {showAddForm ? "إضافة تنبيه جديد" : "تعديل التنبيه الحالي"}
              </h3>
            </div>

            <div className="modal-body">
              <div className="input-field">
                <label>عنوان التنبيه</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faHeading} className="field-icon" />
                  <input
                    type="text"
                    value={showAddForm ? newTitle : editTitle}
                    onChange={(e) =>
                      showAddForm
                        ? setNewTitle(e.target.value)
                        : setEditTitle(e.target.value)
                    }
                    placeholder="مثال: تحديث خلفيات الشهادات"
                  />
                </div>
              </div>

              <div className="input-field">
                <label>رسالة التنبيه</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon
                    icon={faParagraph}
                    className="field-icon"
                    style={{ top: "15px" }}
                  />
                  <textarea
                    ref={showAddForm ? messageRef : null}
                    value={showAddForm ? newMessage : editMessage}
                    onChange={(e) =>
                      showAddForm
                        ? setNewMessage(e.target.value)
                        : setEditMessage(e.target.value)
                    }
                    placeholder="اكتب تفاصيل الإشعار هنا..."
                    style={{ height: "100px", paddingTop: "12px" }}
                  />
                </div>
              </div>

              <div
                className="section-grid"
                style={{ gridTemplateColumns: "1fr 1fr", gap: "15px" }}
              >
                <div className="input-field">
                  <label>نوع الإشعار</label>
                  <select
                    value={showAddForm ? newType : editType}
                    onChange={(e) =>
                      showAddForm
                        ? setNewType(e.target.value)
                        : setEditType(e.target.value)
                    }
                    className="premium-select"
                  >
                    <option value="info">Info (معلومات)</option>
                    <option value="update">Update (تحديث)</option>
                    <option value="alert">Alert (تنبيه خطير)</option>
                  </select>
                </div>
                <div className="input-field">
                  <label>رابط اختياري</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faLink} className="field-icon" />
                    <input
                      type="text"
                      value={showAddForm ? newLink : editLink}
                      onChange={(e) =>
                        showAddForm
                          ? setNewLink(e.target.value)
                          : setEditLink(e.target.value)
                      }
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={showAddForm ? handleSubmitNew : handleSubmitEdit}
                className="premium-submit-btn"
              >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>
                    {showAddForm ? "نشر التنبيه الآن" : "تحديث البيانات"}
                  </span>
                </div>
                <div className="btn-glow"></div>
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setShowEditForm(false);
                }}
                className="cancel-btn"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>إلغاء</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
                .notifications-wrapper { padding: 5px; color: var(--text); font-family: 'Cairo', sans-serif; }
        .premium-edit-container {
          margin: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }

        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 15px; /* Reduced side padding to match table */
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          gap: 20px;
        }
                .header-left { display: flex; align-items: center; gap: 15px; }
                .header-icon-box {
                    width: 48px; height: 48px; background: var(--accent);
                    border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    font-size: 20px; color: white;
                    box-shadow: 0 8px 16px -4px color-mix(in srgb, var(--accent) 40%, transparent);
                }
                .header-text-box h2 { margin: 0; font-size: 20px; font-weight: 800; }
                .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; margin-top: 2px; }
                .breadcrumb-info span:first-child { color: #d97706; font-weight: 500; }
                .current-page { color: var(--accent); font-weight: 600; }

                .header-tools { 
                    display: flex; 
                    align-items: center; 
                    gap: 10px; 
                    justify-content: flex-start;
                    flex-direction: row-reverse; /* Put Delete on the left, Add on the right */
                }

                .premium-action-btn {
                    background: var(--accent); color: white; border: none; border-radius: 12px;
                    padding: 10px 18px; display: flex; align-items: center; gap: 8px;
                    font-size: 13px; font-weight: 700; cursor: pointer; transition: 0.3s;
                    box-shadow: 0 8px 16px -4px color-mix(in srgb, var(--accent) 40%, transparent);
                    white-space: nowrap;
                    flex: 2; /* Bigger button */
                }
                .premium-action-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }

                .bulk-delete-btn-inline {
                    background: #ef4444; color: white; border: none; border-radius: 12px;
                    display: none; align-items: center; gap: 8px;
                    font-size: 12px; font-weight: 700; cursor: pointer;
                    padding: 8px 12px; transition: 0.2s;
                    white-space: nowrap;
                    flex: 1; /* Smaller button */
                }
                .bulk-delete-btn-inline.show { display: flex; }
                .bulk-delete-btn-inline:hover { background: #dc2626; }

        .premium-table-container { 
          padding: 15px; /* Reduced padding on mobile */
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .modern-condensed-table { 
          width: 100%; 
          border-collapse: separate; 
          border-spacing: 0 8px; 
          min-width: 600px; /* Force scroll */
        }
                .modern-condensed-table th { padding: 15px 20px; text-align: right; color: var(--muted); font-weight: 800; font-size: 13px; text-transform: uppercase; }
                .modern-condensed-table tbody tr { background: rgba(255,255,255,0.015); transition: 0.2s; }
                .modern-condensed-table tbody tr td { padding: 12px 20px; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
                .modern-condensed-table tbody tr td:first-child { border-right: 1px solid var(--border); border-radius: 0 14px 14px 0; }
                .modern-condensed-table tbody tr td:last-child { border-left: 1px solid var(--border); border-radius: 14px 0 0 14px; }
                .modern-condensed-table tbody tr:hover { background: rgba(255,255,255,0.04); }
                .modern-condensed-table tr.row-selected { background: rgba(239, 68, 68, 0.04) !important; }

                .info-cell { display: flex; align-items: center; gap: 15px; cursor: pointer; }
                .avatar-minimal {
                    width: 44px; height: 44px; background: var(--panel-2); border: 1px solid var(--border);
                    border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    font-size: 18px; color: var(--accent);
                }
                 .meta-minimal .name { font-weight: 800; font-size: 15px; display: block; color: var(--text); }
                .meta-minimal .code { font-size: 13.5px; color: var(--muted); font-weight: 500; line-height: 1.5; }

                .type-badge-alt {
                    font-size: 11px; font-weight: 800; padding: 4px 12px; border-radius: 8px; text-transform: uppercase;
                }
                .type-badge-alt.info { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
                .type-badge-alt.update { background: rgba(16, 185, 129, 0.1); color: #10b981; }
                .type-badge-alt.alert { background: rgba(239, 68, 68, 0.1); color: #ef4444; }

                 .date-display { display: flex; align-items: center; gap: 8px; color: var(--muted); font-size: 14.5px; font-weight: 700; }

                .row-toggle-btn {
                    background: var(--bg); border: 1px solid var(--border);
                    width: 38px; height: 38px; border-radius: 10px;
                    color: var(--muted); cursor: pointer; transition: 0.3s;
                }
                .row-toggle-btn:hover { color: var(--accent); border-color: var(--accent); transform: rotate(15deg); }

                /* Premium Modal */
                .premium-modal-overlay {
                    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(13, 35, 32, 0.45); backdrop-filter: blur(10px);
                    display: flex; align-items: center; justify-content: center; z-index: 10000;
                    opacity: 0; pointer-events: none; transition: 0.4s;
                }
                .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
                .premium-modal-card {
                    background: var(--panel); border: 1px solid var(--border); width: 100%; max-width: 550px;
                    border-radius: 24px; padding: 30px; transform: translateY(30px) scale(0.95); transition: 0.4s;
                    box-shadow: 0 40px 100px -20px rgba(0,0,0,0.6);
                }
                .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }

                .modal-header { display: flex; align-items: center; gap: 20px; margin-bottom: 25px; }
                .modal-header .icon {
                    width: 50px; height: 50px; background: rgba(14, 165, 233, 0.15);
                    border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    font-size: 22px; color: var(--accent);
                }
                .modal-header h3 { margin: 0; font-size: 20px; font-weight: 800; }

                .input-field { margin-bottom: 20px; }
                .input-field label { display: block; font-size: 13px; font-weight: 700; color: var(--muted); margin-bottom: 10px; margin-right: 5px; }
                .input-wrapper { position: relative; display: flex; align-items: center; }
                .field-icon { position: absolute; right: 18px; color: var(--muted); font-size: 16px; opacity: 0.6; }
                .input-wrapper input, .input-wrapper textarea, .premium-select {
                    width: 100%; height: 48px; background: var(--panel-2); border: 1px solid var(--border);
                    border-radius: 12px; padding: 0 48px 0 20px; color: var(--text); font-family: inherit;
                    font-size: 15px; transition: 0.3s; outline: none;
                }
                .input-wrapper textarea { height: auto; padding-top: 15px; }
                .input-wrapper input:focus, .input-wrapper textarea:focus, .premium-select:focus {
                    border-color: var(--accent); box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 15%, transparent);
                }

                .premium-select { padding: 0 15px; appearance: none; cursor: pointer; }

                .modal-footer { display: flex; gap: 15px; margin-top: 30px; }
                .premium-submit-btn {
                    flex: 2; background: linear-gradient(135deg, #28917f 0%, #1d7567 100%);
                    border: none; color: white; height: 52px; border-radius: 6px;
                    font-size: 16px; font-weight: 700; cursor: pointer; position: relative;
                    overflow: hidden; transition: 0.3s;
                }
                .btn-glow { position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%); opacity: 0; transition: 0.3s; }
                .premium-submit-btn:hover .btn-glow { opacity: 1; }
                .cancel-btn {
                    flex: 1; background: var(--panel-2); border: 1px solid var(--border);
                    color: var(--text); border-radius: 12px; display: flex; align-items: center;
                    justify-content: center; gap: 10px; font-size: 15px; font-weight: 700; cursor: pointer;
                }

                .loader-overlay { padding: 60px; text-align: center; color: var(--muted); display: flex; flex-direction: column; gap: 20px; }
                .premium-loader { width: 40px; height: 40px; border: 3px solid rgba(14, 165, 233, 0.1); border-top-color: var(--accent); border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .empty-ui-state { text-align: center; padding: 100px !important; color: var(--muted); }
                .empty-ui-state .icon { font-size: 60px; opacity: 0.2; margin-bottom: 25px; }
                .empty-ui-state h3 { font-size: 22px; font-weight: 800; margin: 0; color: var(--text); }
                .empty-ui-state p { margin-top: 15px; font-size: 15px; max-width: 400px; margin-inline: auto; line-height: 1.6; }

                .confirm-toast { text-align: center; font-family: 'Cairo', sans-serif; }
                .toast-actions { display: flex; justify-content: center; gap: 12px; margin-top: 18px; }
                .btn-confirm { background: #ef4444; color: white; border: none; padding: 10px 20px; border-radius: 10px; cursor: pointer; font-weight: 700; font-family: 'Cairo', sans-serif; }
                .btn-cancel { background: var(--panel-2); color: var(--text); border: 1px solid var(--border); padding: 10px 20px; border-radius: 10px; cursor: pointer; font-family: 'Cairo', sans-serif; }

                button, .premium-submit-btn, .cancel-btn, .premium-action-btn, .bulk-delete-btn {
                    font-family: 'Cairo', sans-serif !important;
                }

                /* Mobile Card View */
          .mobile-card-view {
            display: none;
            flex-direction: column;
            gap: 15px;
            padding: 15px; /* Restored padding */
            margin-top: 2px; /* Added 2px gap */
            background: rgba(255, 255, 255, 0.7); 
            backdrop-filter: blur(10px);
            border: 1px solid var(--border);
            border-top: none; 
            border-radius: 0 0 24px 24px;
            width: 100%;
            box-sizing: border-box;
          }

          .mobile-card {
            background: var(--panel-2);
            border: 1px solid var(--border);
            border-radius: 12px;
            padding: 15px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 100%;
            box-sizing: border-box;
          }

          .mobile-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 12px;
            border-bottom: 1px solid var(--border);
          }

          .mobile-card-title {
            font-weight: 700;
            font-size: 15px;
            color: var(--text);
          }

          .mobile-card-body {
            display: flex;
            flex-direction: column;
            gap: 8px;
            font-size: 13px;
            color: var(--muted);
          }

          .mobile-card-row {
            display: flex;
            justify-content: space-between;
          }

          .mobile-card-actions {
            margin-top: 5px;
            display: flex;
            justify-content: flex-end;
          }

          @media (max-width: 900px) {
            .premium-table-container { display: none; }
            .mobile-card-view { display: flex; }
            
            .premium-header-modern {
              padding: 14px 18px;
              gap: 10px;
              flex-wrap: nowrap;
              overflow-x: auto;
            }
            .header-left { gap: 10px; justify-content: flex-start; }
            .header-icon-box { width: 40px; height: 40px; font-size: 18px; }
            .header-text-box h2 { font-size: 18px; }
            .breadcrumb-info { font-size: 11px; }
            .header-tools { flex-direction: row; gap: 8px; width: auto; }
            .premium-action-btn .btn-text { display: none; }
            .premium-action-btn { padding: 10px; }
            .avatar-minimal { display: none; }
          }
            `}</style>
    </div>
  );
};

export default Notifications;
